// store/slices/chatInterfaceSlice.ts - Fixed duplicate user message handling
/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSlice, createAsyncThunk, PayloadAction, createSelector } from '@reduxjs/toolkit';
import {
  fetchChatInterfaceConversations,
  fetchChatInterfaceHistory,
  createChatInterfaceConversation,
  renameChatInterfaceConversation,
  deleteChatInterfaceConversation,
  fetchChatbotInputFields,
  ChatbotInputFieldsInfo,
  streamSimpleChatMessage,
  SimpleSSEEventHandlers,
  SimpleChatbotMessageRequest,
  chatInterfaceAsyncManager,
  getExecutionStatus,
  cancelExecution,
  ChatInterfaceConversation,
} from '@/services/chatInterfaceService';

import { ChatMessage, ChatMessageContentItem } from '@/types/ChatMessage';
import { ChatInterfaceState, ChatConversation, AssistantType } from '@/types/ChatInterface';
import { AsyncExecution, ExecutionStatusResponse } from '@/types/AsyncExecution';
import { RootState } from '@/store/store';
import { TimeoutError } from '@/services/sseService';
import { 
  executionReducers, 
  connectionReducers, 
  messageHelpers,
  getExecutionExtraReducerHandlers
} from './executionHelpers';
import { 
  getDefaultValueForType, 
  parseInputValue, 
  processInputValue 
} from '@/utils/chatInputs';

// =================== Initial State ===================

const initialState: ChatInterfaceState = {
  conversations: [],
  activeConversationId: null,
  activeAssistantType: 'simple',
  messages: [],
  isLoading: false,
  error: null,
  
  activeChatbotInputFields: null,
  additionalInputs: [],
  isLoadingInputFields: false,
  
  isShowingTimeoutNotification: false,
  timeoutError: null,
  
  asyncExecutions: {},
  currentExecutionId: null,
  
  streamConnections: {},
  connectionHealth: {
    status: 'healthy',
    activeConnections: 0,
    failedConnections: 0,
    averageReconnectionTime: 0
  },
};

// =================== Helper Functions ===================

const mapBackendToChatConversation = (backendConv: ChatInterfaceConversation): ChatConversation => {
  return {
    id: backendConv.id,
    title: backendConv.title,
    chatbotId: backendConv.chatbot_id,
    assistantType: backendConv.chatbot_type,
    lastUpdated: backendConv.updated_at,
    lastMessagePreview: backendConv.last_message_preview || '',
  };
};

// =================== Async Thunks ===================

// Input Fields
export const fetchChatbotInputFieldsData = createAsyncThunk<
  ChatbotInputFieldsInfo, 
  string, 
  { rejectValue: string }
>(
  'chatInterface/fetchChatbotInputFields',
  async (chatbotId, { rejectWithValue }) => {
    try {
      return await fetchChatbotInputFields(chatbotId);
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch input fields');
    }
  }
);

// Conversations
export const fetchConversations = createAsyncThunk<
  ChatConversation[], 
  void, 
  { rejectValue: string; state: RootState }
>(
  'chatInterface/fetchConversations',
  async (_, { rejectWithValue, getState }) => {
    const userId = getState().auth.user?.id;
    if (!userId) return rejectWithValue('User not authenticated');
    try {
      const backendConversations = await fetchChatInterfaceConversations();
      return backendConversations.map(mapBackendToChatConversation);
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch conversations');
    }
  }
);

export const fetchConversationHistory = createAsyncThunk<
  { conversationId: string; history: ChatMessage[] },
  string,
  { rejectValue: string }
>(
  'chatInterface/fetchConversationHistory',
  async (conversationId, { rejectWithValue }) => {
    try {
      const rawHistory = await fetchChatInterfaceHistory(conversationId);
      const mappedHistory = rawHistory.map(msg => ({
        ...msg,
        content: messageHelpers.normalizeContent(msg.content)
      })) as ChatMessage[];
      return { conversationId, history: mappedHistory };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch history');
    }
  }
);

export const createConversation = createAsyncThunk<
  ChatConversation,
  { title: string; chatbotId: string; assistantType: AssistantType },
  { rejectValue: string; state: RootState }
>(
  'chatInterface/createConversation',
  async ({ title, chatbotId, assistantType }, { rejectWithValue, getState }) => {
    const userId = getState().auth.user?.id;
    if (!userId) return rejectWithValue('User not authenticated');
    try {
      const conversationId = await createChatInterfaceConversation({ title, chatbot_id: chatbotId });
      return {
        id: conversationId,
        title,
        chatbotId,
        assistantType,
        lastUpdated: new Date().toISOString(),
        lastMessagePreview: ''
      };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create conversation');
    }
  }
);

export const renameConversation = createAsyncThunk<
  { conversationId: string; title: string },
  { conversationId: string; title: string },
  { rejectValue: string }
>(
  'chatInterface/renameConversation',
  async ({ conversationId, title }, { rejectWithValue }) => {
    try {
      await renameChatInterfaceConversation(conversationId, title);
      return { conversationId, title };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to rename');
    }
  }
);

export const deleteConversation = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>(
  'chatInterface/deleteConversation',
  async (conversationId, { rejectWithValue }) => {
    try {
      await deleteChatInterfaceConversation(conversationId);
      return conversationId;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete');
    }
  }
);

// Async Execution
export const startEnhancedAsyncExecution = createAsyncThunk<
  { response: any; executionData: AsyncExecution },
  {
    conversationId: string;
    assistantType: AssistantType;
    currentUserMessageContent: ChatMessageContentItem[];
    additionalInputs?: Record<string, any>;
  },
  { rejectValue: string; dispatch: any; state: RootState }
>(
  'chatInterface/startEnhancedAsyncExecution',
  async (payload, { rejectWithValue, dispatch }) => {
    const { conversationId, assistantType, currentUserMessageContent, additionalInputs = {} } = payload;



    try {
      let messageData: any;

      if (assistantType === 'simple') {
        messageData = {
          messages: [{ role: 'user', content: currentUserMessageContent }],
        };
      } else {
        let queryValue: string | ChatMessageContentItem[];
        if (currentUserMessageContent.length === 1 && currentUserMessageContent[0].type === 'text') {
          queryValue = currentUserMessageContent[0].text;
        } else {
          queryValue = currentUserMessageContent;
        }

        const processedAdditionalInputs: Record<string, any> = {};
        for (const [key, value] of Object.entries(additionalInputs)) {
          processedAdditionalInputs[key] = processInputValue(value);
        }

        messageData = { 
          input_data: { 
            ...processedAdditionalInputs, 
            query: queryValue 
          } 
        };
      }

      // Use the chatInterfaceAsyncManager to start execution with streaming
      const executionId = await chatInterfaceAsyncManager.startAsyncExecution(
        conversationId,
        assistantType,
        messageData,
        {
          onExecutionStarted: () => { // (execution)
            //console.log('[Redux Thunk] Execution started:', execution.execution_id);
          },
          onExecutionProgress: (event) => {
            // Update execution state in Redux
            dispatch(chatInterfaceSlice.actions.updateExecutionFromStream({
              executionId: event.execution_id,
              event
            }));
          },
          onExecutionCompleted: () => { // (execution)
            //console.log('[Redux Thunk] Execution completed:', execution.execution_id);
          },
          onExecutionFailed: (execution, error) => {
            //console.log('[Redux Thunk] Execution failed:', execution.execution_id, error);
            dispatch(chatInterfaceSlice.actions.setExecutionError({
              executionId: execution.execution_id,
              error
            }));
          },
          onConnectionUpdate: (connection) => {
            dispatch(chatInterfaceSlice.actions.updateStreamConnection(connection));
          },
          onConnectionError: (error) => {
            console.error('[Redux Thunk] Connection error:', error);
          }
        }
      );

      // Get the execution data for Redux state
      const executionData: AsyncExecution = {
        execution_id: executionId,
        status: 'RUNNING',
        progress: 0,
        current_node: null,
        completed_nodes: 0,
        total_nodes: 0,
        final_output: null,
        error_message: null,
        started_at: new Date().toISOString(),
        completed_at: null,
        execution_source: 'chatbot',
        conversation_id: conversationId,
        assistant_type: assistantType,
      };

      return { 
        response: { execution_id: executionId }, 
        executionData 
      };

    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to start async execution');
    }
  }
);

export const cancelEnhancedAsyncExecution = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>(
  'chatInterface/cancelEnhancedAsyncExecution',
  async (executionId, { rejectWithValue }) => {
    try {
      await cancelExecution(executionId);
      return executionId;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to cancel');
    }
  }
);

export const refreshExecutionStatus = createAsyncThunk<
  ExecutionStatusResponse,
  string,
  { rejectValue: string }
>(
  'chatInterface/refreshExecutionStatus',
  async (executionId, { rejectWithValue }) => {
    try {
      return await getExecutionStatus(executionId);
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to refresh');
    }
  }
);

// Sync Message Sending
export const sendMessage = createAsyncThunk<
  { streamingInitiated: boolean },
  {
    conversationId: string;
    assistantType: AssistantType;
    currentUserMessageContent: ChatMessageContentItem[];
    additionalInputs?: Record<string, any>;
  },
  { rejectValue: string; dispatch: any }
>(
  'chatInterface/sendMessage',
  async (payload, { rejectWithValue, dispatch }) => {
    const { conversationId, assistantType, currentUserMessageContent } = payload;

    dispatch(chatInterfaceSlice.actions.addAssistantMessagePlaceholder());
    dispatch(chatInterfaceSlice.actions.setIsLoading(true));

    try {
      if (assistantType === 'simple') {
        const requestData: SimpleChatbotMessageRequest = {
          messages: [{ role: 'user', content: currentUserMessageContent }],
        };

        return new Promise((resolve, rejectPromise) => {
          const handlers: SimpleSSEEventHandlers = {
            onStreamStart: () => {},
            onChunk: (chunk) => {
              dispatch(chatInterfaceSlice.actions.updateLastMessageContent({ chunk, replace: false }));
            },
            onStreamEnd: () => {
              dispatch(chatInterfaceSlice.actions.setIsLoading(false));
              dispatch(chatInterfaceSlice.actions.touchConversation(conversationId));
              resolve({ streamingInitiated: true });
            },
            onError: (error: Error) => {
              dispatch(chatInterfaceSlice.actions.setLastMessageError(error.message));
              dispatch(chatInterfaceSlice.actions.setIsLoading(false));
              rejectPromise(rejectWithValue(error.message));
            },
            onTimeoutError: (timeoutError: TimeoutError) => {
              dispatch(chatInterfaceSlice.actions.setTimeoutError({
                message: timeoutError.message,
                statusCode: timeoutError.statusCode
              }));
              dispatch(chatInterfaceSlice.actions.setIsLoading(false));
              rejectPromise(rejectWithValue(timeoutError.message));
            },
          };
          streamSimpleChatMessage(conversationId, requestData, handlers);
        });

      } else {
        dispatch(chatInterfaceSlice.actions.setIsLoading(false));
        return rejectWithValue(`${assistantType} should use async execution`);
      }
    } catch (error: any) {
      dispatch(chatInterfaceSlice.actions.setIsLoading(false));
      dispatch(chatInterfaceSlice.actions.setLastMessageError(error.message || 'Failed to send'));
      return rejectWithValue(error.message || 'Failed to send');
    }
  }
);

// =================== Slice Definition ===================

const chatInterfaceSlice = createSlice({
  name: 'chatInterface',
  initialState,
  reducers: {
    // Conversation Management
    setActiveConversation: (state, action: PayloadAction<string | null>) => {
      const newActiveId = action.payload;
      const previousActiveId = state.activeConversationId;
      
      // FIX: Be more careful about when to clear messages
      if (previousActiveId !== newActiveId) {
        // Only clear messages when actually switching between different existing conversations
        // Don't clear when going from null to new conversation (new conversation creation)
        if (previousActiveId !== null && newActiveId !== null) {
          // Switching between existing conversations
          state.messages = [];
          state.error = null;
          state.isShowingTimeoutNotification = false;
          state.timeoutError = null;
          state.currentExecutionId = null;
          state.activeChatbotInputFields = null;
          state.additionalInputs = [];

        } else if (previousActiveId !== null && newActiveId === null) {
          // Going from conversation to no conversation (new conversation button)
          state.messages = [];
          state.error = null;
          state.isShowingTimeoutNotification = false;
          state.timeoutError = null;
          state.currentExecutionId = null;
          state.activeChatbotInputFields = null;
          state.additionalInputs = [];
        }
      }
      
      state.activeConversationId = newActiveId;
      
      if (newActiveId) {
        const newActiveConv = state.conversations.find(c => c.id === newActiveId);
        state.activeAssistantType = newActiveConv?.assistantType || 'simple';
      } else {
        state.activeAssistantType = 'simple';
      }
    },

    setActiveAssistantType: (state, action: PayloadAction<AssistantType>) => {
      state.activeAssistantType = action.payload;
    },

    // Input Field Management
    initializeAdditionalInputs: (state, action: PayloadAction<ChatbotInputFieldsInfo>) => {
      const inputFieldsInfo = action.payload;
      state.activeChatbotInputFields = inputFieldsInfo;
      
      if (inputFieldsInfo.has_additional_inputs) {
        state.additionalInputs = inputFieldsInfo.input_fields.map(field => ({
          name: field.name,
          type: field.type,
          isMandatory: field.is_mandatory,
          value: getDefaultValueForType(field.type),
        }));
      } else {
        state.additionalInputs = [];
      }
    },

    updateAdditionalInput: (state, action: PayloadAction<{ name: string; value: any }>) => {
      const { name, value } = action.payload;
      const fieldIndex = state.additionalInputs.findIndex(field => field.name === name);
      if (fieldIndex !== -1) {
        const field = state.additionalInputs[fieldIndex];
        try {
          state.additionalInputs[fieldIndex].value = parseInputValue(field.type, value);
        } catch (error) {
          console.warn(`Failed to parse input value for ${name}:`, error);
          state.additionalInputs[fieldIndex].value = value;
        }
      }
    },

    clearAdditionalInputs: (state) => {
      state.additionalInputs = state.additionalInputs.map(field => ({
        ...field,
        value: getDefaultValueForType(field.type),
      }));
    },

    // Message Management
    addMessage: (state, action: PayloadAction<ChatMessage>) => {
      messageHelpers.addMessage(state, action.payload);
    },

    addAssistantMessagePlaceholder: (state) => {
      messageHelpers.addAssistantPlaceholder(state);
      state.isShowingTimeoutNotification = false;
      state.timeoutError = null;
    },

    updateLastMessageContent: (state, action: PayloadAction<{ chunk: string; replace?: boolean }>) => {
      const { chunk, replace } = action.payload;
      messageHelpers.updateLastMessageContent(state, chunk, replace || false);
      if (state.isShowingTimeoutNotification) {
        state.isShowingTimeoutNotification = false;
        state.timeoutError = null;
      }
    },

    setLastMessageError: (state, action: PayloadAction<string>) => {
      const errorMessageText = `Error: ${action.payload}`;
      
      if (state.messages.length > 0) {
        const lastMessageIndex = state.messages.length - 1;
        if (state.messages[lastMessageIndex].role === 'assistant') {
          state.messages[lastMessageIndex].content = [{ type: 'text', text: errorMessageText }];
          (state.messages[lastMessageIndex] as any).error = true;
          (state.messages[lastMessageIndex] as any).isLoading = false;
          return;
        }
      }
      state.messages.push({ 
        role: 'assistant', 
        content: [{ type: 'text', text: errorMessageText }], 
        error: true, 
        isLoading: false 
      } as any);

      if (!action.payload.includes('timed out')) {
        state.isShowingTimeoutNotification = false;
        state.timeoutError = null;
      }
    },

    // Execution Management (from helpers)
    ...executionReducers,
    ...connectionReducers,

    // General State
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
      if (!action.payload && !state.timeoutError) {
        state.isShowingTimeoutNotification = false;
      }
    },

    touchConversation: (state, action: PayloadAction<string | null>) => {
      const convId = action.payload;
      if (!convId) return;
      const activeConvIndex = state.conversations.findIndex(c => c.id === convId);
      if (activeConvIndex !== -1) {
        const conv = state.conversations[activeConvIndex];
        conv.lastUpdated = new Date().toISOString();
        if (activeConvIndex > 0) {
          state.conversations.splice(activeConvIndex, 1);
          state.conversations.unshift(conv);
        }
      }
    },

    setTimeoutError: (state, action: PayloadAction<{ message: string; statusCode: number }>) => {
      state.timeoutError = action.payload;
      state.isShowingTimeoutNotification = true;
      
      const lastMessageIndex = state.messages.length - 1;
      if (lastMessageIndex >= 0 && state.messages[lastMessageIndex].role === 'assistant') {
        state.messages[lastMessageIndex].content = [{ type: 'text', text: `Error: ${action.payload.message}` }];
        (state.messages[lastMessageIndex] as any).error = true;
        (state.messages[lastMessageIndex] as any).isLoading = false;
      }
    },

    clearTimeoutError: (state) => {
      state.timeoutError = null;
      state.isShowingTimeoutNotification = false;
    },

    clearMessages: (state) => {
      state.messages = [];
      state.error = null;
      state.isShowingTimeoutNotification = false;
      state.timeoutError = null;
    },

    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },

    resetChatInterface: () => initialState,
  },

  extraReducers: (builder) => {
    const handlers = getExecutionExtraReducerHandlers();

    builder
      // Fetch conversations
      .addCase(fetchConversations.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchConversations.fulfilled, (state, action) => {
        state.isLoading = false;
        state.conversations = action.payload;
      })
      .addCase(fetchConversations.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? 'Failed to fetch conversations';
      })

      // Fetch history
      .addCase(fetchConversationHistory.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchConversationHistory.fulfilled, (state, action) => {
        state.isLoading = false;
        if (state.activeConversationId === action.payload.conversationId) {
          if (action.payload.history && action.payload.history.length > 0) {
            state.messages = action.payload.history;
          }
        }
      })
      .addCase(fetchConversationHistory.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? 'Failed to fetch history';
      })

      // Create conversation
      .addCase(createConversation.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createConversation.fulfilled, (state, action) => {
        state.isLoading = false;
        state.conversations.unshift(action.payload);
        state.activeConversationId = action.payload.id;
        state.activeAssistantType = action.payload.assistantType;
        state.error = null;
      })
      .addCase(createConversation.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? 'Failed to create conversation';
      })

      // Rename conversation
      .addCase(renameConversation.fulfilled, (state, action) => {
        const index = state.conversations.findIndex(c => c.id === action.payload.conversationId);
        if (index !== -1) {
          state.conversations[index].title = action.payload.title;
          state.conversations[index].lastUpdated = new Date().toISOString();
        }
      })

      // Delete conversation
      .addCase(deleteConversation.fulfilled, (state, action) => {
        const deletedId = action.payload;
        state.conversations = state.conversations.filter(c => c.id !== deletedId);
        if (state.activeConversationId === deletedId) {
          const nextConv = state.conversations[0];
          state.activeConversationId = nextConv?.id || null;
          state.activeAssistantType = nextConv?.assistantType || 'simple';
          state.messages = [];
          state.error = null;
        }
      })

      // Fetch input fields
      .addCase(fetchChatbotInputFieldsData.pending, (state) => {
        state.isLoadingInputFields = true;
      })
      .addCase(fetchChatbotInputFieldsData.fulfilled, (state, action) => {
        state.isLoadingInputFields = false;
        chatInterfaceSlice.caseReducers.initializeAdditionalInputs(state, action);
      })
      .addCase(fetchChatbotInputFieldsData.rejected, (state, action) => {
        state.isLoadingInputFields = false;
        state.error = action.payload ?? 'Failed to fetch input fields';
      })

      .addCase(startEnhancedAsyncExecution.pending, handlers.handleStartExecutionPending)
      .addCase(startEnhancedAsyncExecution.fulfilled, (state, action) => {
        const { response, executionData } = action.payload;
        state.isLoading = true; // Keep loading for async
        state.currentExecutionId = response.execution_id;
        state.asyncExecutions[response.execution_id] = executionData;        
      })
      .addCase(startEnhancedAsyncExecution.rejected, handlers.handleStartExecutionRejected)

      // Cancel execution
      .addCase(cancelEnhancedAsyncExecution.fulfilled, handlers.handleCancelExecutionFulfilled)

      // Refresh execution status
      .addCase(refreshExecutionStatus.fulfilled, (state, action) => {
        executionReducers.updateExecutionStatus(state, action.payload as any);
      });
  }
});

// =================== Selectors ===================

export const selectActiveExecution = createSelector(
  [(state: RootState) => state.chatInterface.currentExecutionId,
   (state: RootState) => state.chatInterface.asyncExecutions],
  (currentId, executions) => currentId ? executions[currentId] : null
);

export const selectConnectionHealth = createSelector(
  [(state: RootState) => state.chatInterface.connectionHealth],
  (health) => health
);

// =================== Exports ===================

export const {
  setActiveConversation,
  setActiveAssistantType,
  updateAdditionalInput,
  clearAdditionalInputs,
  updateExecutionFromStream,
  setExecutionError,
  clearAsyncExecutions,
  updateStreamConnection,
  removeStreamConnection,
  setIsLoading,
  clearTimeoutError,
  clearMessages,
  updateLastMessageContent,
  addMessage,
  addAssistantMessagePlaceholder,
  setLastMessageError,
  touchConversation,
} = chatInterfaceSlice.actions;

export default chatInterfaceSlice.reducer;